/**
 * DashboardCalendar — compact 30-day agenda view for the landlord and
 * tenant dashboards. Fetches /api/calendar/events with the user_id +
 * role from useAuth so it shows the right rent-due / lease-renewal /
 * maintenance / showing / inspection events.
 *
 * Click "Add event" to drop a custom item into db.calendar_events.
 */
import { useEffect, useState, useMemo } from "react";
import { Calendar as CalIcon, Plus, X, Trash2, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { confirmDialog } from "@/components/ConfirmDialog";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const CAT_STYLE = {
  rent_due:       { bg: "bg-emerald-50",  fg: "text-emerald-800",  dot: "bg-emerald-500" },
  lease_renewal:  { bg: "bg-amber-50",    fg: "text-amber-800",    dot: "bg-amber-500" },
  maintenance:    { bg: "bg-blue-50",     fg: "text-blue-800",     dot: "bg-blue-500" },
  showing:        { bg: "bg-violet-50",   fg: "text-violet-800",   dot: "bg-violet-500" },
  inspection:     { bg: "bg-rose-50",     fg: "text-rose-800",     dot: "bg-rose-500" },
  custom:         { bg: "bg-slate-50",    fg: "text-slate-800",    dot: "bg-slate-500" },
};

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" });
  } catch (e) { return iso; }
}
function fmtTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" });
  } catch (e) { return ""; }
}
function dayKey(iso) {
  return (iso || "").slice(0, 10);
}

export default function DashboardCalendar({ role = "landlord" }) {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState({ title: "", starts_at: "", location: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const userId = user?.user_id || user?.id || user?._id;

  async function load() {
    if (!userId) return;
    setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/api/calendar/events?user_id=${userId}&role=${role}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId, "x-user-role": role },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      setEvents(d.events || []);
    } catch (e) { setError(String(e.message || e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId, role]);

  async function addEvent(e) {
    e.preventDefault();
    if (!draft.title || !draft.starts_at) { setError("Title + date required"); return; }
    setSaving(true); setError("");
    try {
      const iso = new Date(draft.starts_at).toISOString();
      const r = await fetch(`${API}/api/calendar/events?user_id=${userId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId },
        body: JSON.stringify({ title: draft.title, starts_at: iso, location: draft.location || null, notes: draft.notes || null, category: "custom" }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      setDraft({ title: "", starts_at: "", location: "", notes: "" });
      setShowAdd(false);
      await load();
    } catch (e) { setError(String(e.message || e)); }
    finally { setSaving(false); }
  }

  async function deleteEvent(id) {
    if (!id.startsWith("ev_")) return; // only custom events deletable
    const ok = await confirmDialog({
      title: "Delete this event?",
      description: "This calendar event will be removed permanently.",
      confirmLabel: "Yes, delete",
      cancelLabel: "Keep event",
      destructive: true,
    });
    if (!ok) return;
    try {
      const r = await fetch(`${API}/api/calendar/events/${id}?user_id=${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId },
      });
      if (!r.ok) throw new Error("delete failed");
      await load();
    } catch (e) { setError(String(e.message || e)); }
  }

  const grouped = useMemo(() => {
    const g = new Map();
    for (const ev of events) {
      const k = dayKey(ev.starts_at);
      if (!g.has(k)) g.set(k, []);
      g.get(k).push(ev);
    }
    return [...g.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [events]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`dashboard-calendar-${role}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalIcon className="w-5 h-5 text-emerald-600"/>
          <h2 className="font-display text-base md:text-lg font-extrabold text-slate-900">Calendar</h2>
          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Next 30 days</span>
        </div>
        <button onClick={() => setShowAdd(s => !s)} className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-900" data-testid="calendar-add-toggle">
          {showAdd ? <><X className="w-3 h-3"/> Cancel</> : <><Plus className="w-3 h-3"/> Add event</>}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={addEvent} className="mb-4 p-3 rounded-lg border border-slate-200 bg-slate-50 space-y-2" data-testid="calendar-add-form">
          <input type="text" required value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Event title" className="w-full px-3 py-2 text-xs border border-slate-300 rounded-md outline-none focus:border-emerald-500"/>
          <input type="datetime-local" required value={draft.starts_at} onChange={(e) => setDraft({ ...draft, starts_at: e.target.value })} className="w-full px-3 py-2 text-xs border border-slate-300 rounded-md outline-none focus:border-emerald-500"/>
          <input type="text" value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} placeholder="Location (optional)" className="w-full px-3 py-2 text-xs border border-slate-300 rounded-md outline-none focus:border-emerald-500"/>
          <textarea value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} placeholder="Notes (optional)" rows={2} className="w-full px-3 py-2 text-xs border border-slate-300 rounded-md outline-none focus:border-emerald-500"/>
          <button type="submit" disabled={saving} className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-50" data-testid="calendar-add-submit">
            {saving ? <Loader2 className="w-3 h-3 animate-spin inline mr-1"/> : null}
            {saving ? "Saving…" : "Add event"}
          </button>
        </form>
      )}

      {loading ? (
        <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-600 inline"/></div>
      ) : error ? (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded">{error}</div>
      ) : grouped.length === 0 ? (
        <div className="py-6 text-center text-xs text-slate-400 border border-dashed border-slate-200 rounded-lg">No events in the next 30 days. Add one above.</div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto pr-1" data-testid="calendar-events-list">
          {grouped.map(([day, list]) => (
            <div key={day}>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">{fmtDate(day + "T12:00:00Z")}</div>
              <div className="space-y-1.5">
                {list.map((ev) => {
                  const style = CAT_STYLE[ev.category] || CAT_STYLE.custom;
                  return (
                    <div key={ev.id} className={`flex items-start gap-2 px-3 py-2 rounded-md ${style.bg} ${style.fg}`} data-testid={`calendar-event-${ev.id}`}>
                      <div className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${style.dot}`}/>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold truncate">{ev.title}</div>
                        <div className="text-[10px] opacity-80">{fmtTime(ev.starts_at)}{ev.location ? ` · ${ev.location}` : ""}</div>
                        {ev.notes && <div className="text-[10px] opacity-70 truncate">{ev.notes}</div>}
                      </div>
                      {ev.id?.startsWith("ev_") && (
                        <button onClick={() => deleteEvent(ev.id)} className="text-current opacity-50 hover:opacity-100" title="Delete">
                          <Trash2 className="w-3 h-3"/>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
